import axios from 'axios';
import { toast } from 'react-toastify';

// Detectar URL do backend automaticamente:
// - Em produção: usa REACT_APP_API_URL do .env
// - Em dev local (localhost): usa localhost:3001
// - Em dev pela rede (ex: celular acessando 192.168.x.x:3000): usa o mesmo host, porta 3001
const detectarBaseURL = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') {
    return 'http://localhost:3001/api';
  }
  return `http://${hostname}:3001/api`;
};

// Configuração base do axios
const api = axios.create({
  baseURL: detectarBaseURL(),
  timeout: 30000,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Interceptor para requisições - adicionar token automaticamente
api.interceptors.request.use(
  (config) => {
    // Adicionar token se existir
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }

    // FormData: o browser precisa definir o boundary do multipart.
    // O default application/json do axios quebra o upload no iPhone Safari.
    if (typeof FormData !== 'undefined' && config.data instanceof FormData) {
      const h = config.headers;
      if (h) {
        if (typeof h.set === 'function') h.set('Content-Type', false);
        else if (typeof h.delete === 'function') h.delete('Content-Type');
        else {
          delete h['Content-Type'];
          delete h['content-type'];
        }
      }
    }

    console.log(`🚀 ${config.method?.toUpperCase()} ${config.url}`, config.data);
    return config;
  },
  (error) => {
    console.error('❌ Erro na requisição:', error);
    return Promise.reject(error);
  }
);

// Interceptor para respostas
api.interceptors.response.use(
  (response) => {
    console.log(`✅ ${response.config.method?.toUpperCase()} ${response.config.url} - ${response.status}`);
    return response;
  },
  (error) => {
    console.error('❌ Erro na resposta:', error);
    
    // Se receber erro 401, remover token e redirecionar para login
    if (error.response?.status === 401) {
      localStorage.removeItem('token');
      // Redirecionar para login se não estiver já lá
      if (!window.location.pathname.includes('/login')) {
        window.location.href = '/login';
      }
    }
    
    // Tratamento de erros
    let message = 'Erro interno do servidor';
    
    if (error.response) {
      // Erro com resposta do servidor
      const { status, data } = error.response;
      
      switch (status) {
        case 400:
          message = data.error || data.message || 'Dados inválidos';
          break;
        case 401:
          message = 'Não autorizado - faça login novamente';
          break;
        case 403:
          message = 'Acesso negado';
          break;
        case 404:
          message = data.error || data.message || 'Recurso não encontrado';
          break;
        case 409:
          message = data.error || data.message || 'Conflito de dados';
          break;
        case 429:
          message = 'Muitas tentativas de login. Aguarde um momento e tente novamente.';
          break;
        case 422:
          message = data.error || data.message || 'Dados de validação inválidos';
          break;
        case 500:
          message = data.error || data.message || 'Erro interno do servidor';
          break;
        default:
          message = data.error || data.message || `Erro ${status}`;
      }
    } else if (error.request) {
      // Erro de rede
      message = 'Erro de conexão com o servidor';
    }
    
    // Não exibir toast automático - deixar para os componentes controlarem
    
    return Promise.reject({
      ...error,
      message,
    });
  }
);

// Funções utilitárias
export const handleApiError = (error) => {
  console.error('API Error:', error);
  
  if (error.response?.data?.error) {
    return error.response.data.error;
  }
  
  if (error.response?.data?.message) {
    return error.response.data.message;
  }
  
  return error.message || 'Erro desconhecido';
};

export const formatApiResponse = (response) => {
  return {
    success: response.data.success || true,
    data: response.data.data || response.data,
    message: response.data.message,
    total: response.data.total,
  };
};

// Serviços de Autenticação
export const authService = {
  // Login
  async login(email, senha) {
    try {
      const response = await api.post('/auth/login', { email, senha });
      return {
        success: response.data.success,
        token: response.data.token,
        user: response.data.user,
        message: response.data.message
      };
    } catch (error) {
      throw error;
    }
  },

  // Verificar token / Buscar usuário atual
  async getMe() {
    try {
      const response = await api.get('/auth/me');
      return {
        success: response.data.success,
        user: response.data.user,
        message: response.data.message
      };
    } catch (error) {
      throw error;
    }
  },

  // Verificar token (alias para getMe)
  async verifyToken() {
    return this.getMe();
  },

  // Logout
  async logout() {
    try {
      const response = await api.post('/auth/logout');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Alterar senha
  async changePassword(dados) {
    try {
      const response = await api.put('/auth/change-password', dados);
      toast.success(response.data.message || 'Senha alterada com sucesso!');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar próprio perfil
  async getProfile() {
    try {
      const response = await api.get('/auth/profile');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Atualizar próprio perfil
  async updateProfile(dados) {
    try {
      const response = await api.put('/auth/profile', dados);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Configurar token no header
  setAuthToken: (token) => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    } else {
      delete api.defaults.headers.common['Authorization'];
    }
  },

  // Remover token
  removeAuthToken: () => {
    delete api.defaults.headers.common['Authorization'];
  }
};

// Serviços para Usuários
export const userService = {
  // Listar todos os usuários
  async getAll() {
    try {
      const response = await api.get('/users');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar usuário por ID
  async getById(id) {
    try {
      const response = await api.get(`/users/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Criar novo usuário
  async create(dados) {
    try {
      const response = await api.post('/users', dados);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Atualizar usuário
  async update(id, dados) {
    try {
      const response = await api.put(`/users/${id}`, dados);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Deletar usuário
  async delete(id) {
    try {
      const response = await api.delete(`/users/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  }
};

// Serviços para Colaboradores
export const colaboradorService = {
  // Listar todos os colaboradores
  async getAll(filtros = {}) {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filtros).forEach(key => {
        if (filtros[key] !== undefined && filtros[key] !== '') {
          params.append(key, filtros[key]);
        }
      });
      
      const response = await api.get(`/colaboradores?${params}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar colaborador por ID
  async getById(id) {
    try {
      const response = await api.get(`/colaboradores/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Criar novo colaborador
  async create(dados) {
    try {
      const response = await api.post('/colaboradores', dados);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Atualizar colaborador
  async update(id, dados) {
    try {
      const response = await api.put(`/colaboradores/${id}`, dados);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Deletar colaborador (soft delete)
  async delete(id) {
    try {
      const response = await api.delete(`/colaboradores/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar estatísticas de presença
  async getEstatisticas(id) {
    try {
      const response = await api.get(`/colaboradores/${id}/estatisticas`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar departamentos únicos
  async getDepartamentos() {
    try {
      const response = await api.get('/colaboradores/departamentos/lista');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  async addCargos(colaboradorId, cargoIds) {
    try {
      const response = await api.post(`/colaboradores/${colaboradorId}/cargos`, {
        cargo_ids: cargoIds
      });
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  async removeCargo(colaboradorId, cargoId) {
    try {
      const response = await api.delete(`/colaboradores/${colaboradorId}/cargos/${cargoId}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },
};

// Serviços para Reuniões
export const reuniaoService = {
  // Listar todas as reuniões
  async getAll(filtros = {}) {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filtros).forEach(key => {
        if (filtros[key] !== undefined && filtros[key] !== '') {
          params.append(key, filtros[key]);
        }
      });
      
      const response = await api.get(`/reunioes?${params}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar reunião por ID
  async getById(id) {
    try {
      const response = await api.get(`/reunioes/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar reunião com participantes
  async getComParticipantes(id) {
    try {
      const response = await api.get(`/reunioes/${id}/participantes`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Criar nova reunião
  async create(dados) {
    try {
      const response = await api.post('/reunioes', dados);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Atualizar reunião
  async update(id, dados) {
    try {
      const response = await api.put(`/reunioes/${id}`, dados);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Deletar reunião
  async delete(id) {
    try {
      const response = await api.delete(`/reunioes/${id}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Adicionar participantes à reunião
  async adicionarParticipantes(id, colaboradores_ids) {
    try {
      const response = await api.post(`/reunioes/${id}/participantes`, {
        colaboradores_ids
      });
      toast.success(response.data.message || 'Participantes adicionados com sucesso!');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Remover participante da reunião
  async removerParticipante(reuniao_id, colaborador_id) {
    try {
      const response = await api.delete(`/reunioes/${reuniao_id}/participantes/${colaborador_id}`);
      toast.success(response.data.message || 'Participante removido com sucesso!');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Confirmar participação
  async confirmarParticipacao(reuniao_id, colaborador_id) {
    try {
      const response = await api.put(`/reunioes/${reuniao_id}/participantes/${colaborador_id}/confirmar`);
      toast.success(response.data.message || 'Participação confirmada!');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar próximas reuniões
  async getProximas(limite = 5) {
    try {
      const response = await api.get(`/reunioes/proximas?limite=${limite}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar estatísticas da reunião
  async getEstatisticas(id) {
    try {
      const response = await api.get(`/reunioes/${id}/estatisticas`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar reuniões por período
  async getPorPeriodo(data_inicio, data_fim) {
    try {
      const response = await api.get(`/reunioes/periodo/${data_inicio}/${data_fim}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },
};

// Serviços para Presenças
export const presencaService = {
  // Listar todas as presenças
  async getAll(filtros = {}) {
    try {
      const params = new URLSearchParams();
      
      Object.keys(filtros).forEach(key => {
        if (filtros[key] !== undefined && filtros[key] !== '') {
          params.append(key, filtros[key]);
        }
      });
      
      const response = await api.get(`/presencas?${params}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar presenças por reunião
  async getPorReuniao(reuniao_id) {
    try {
      const response = await api.get(`/presencas/reuniao/${reuniao_id}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar presenças por colaborador
  async getPorColaborador(colaborador_id) {
    try {
      const response = await api.get(`/presencas/colaborador/${colaborador_id}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Registrar presença
  async registrar(dados) {
    try {
      const response = await api.post('/presencas', dados);
      toast.success(response.data.message || 'Presença registrada com sucesso!');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Registrar presenças em lote
  async registrarLote(presencas) {
    try {
      const response = await api.post('/presencas/lote', { presencas });
      toast.success(response.data.message || 'Presenças registradas com sucesso!');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Inicializar presenças para reunião
  async inicializar(reuniao_id) {
    try {
      const response = await api.post(`/presencas/inicializar/${reuniao_id}`);
      if (response.data.total > 0) {
        toast.success(response.data.message || 'Presenças inicializadas com sucesso!');
      } else {
        toast.info('Presenças já foram inicializadas para esta reunião');
      }
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Atualizar presença
  async update(id, dados) {
    try {
      const response = await api.put(`/presencas/${id}`, dados);
      toast.success(response.data.message || 'Presença atualizada com sucesso!');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Deletar presença
  async delete(id) {
    try {
      const response = await api.delete(`/presencas/${id}`);
      toast.success(response.data.message || 'Presença removida com sucesso!');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar ranking de presença
  async getRanking(limite = 10) {
    try {
      const response = await api.get(`/presencas/ranking?limite=${limite}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Buscar estatísticas por período
  async getEstatisticasPorPeriodo(data_inicio, data_fim) {
    try {
      const response = await api.get(`/presencas/estatisticas/periodo/${data_inicio}/${data_fim}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },
};

// Serviços para Dashboard
export const dashboardService = {
  // Resumo geral
  async getResumo() {
    try {
      const response = await api.get('/dashboard/resumo');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Presença por mês
  async getPresencaPorMes(ano) {
    try {
      const response = await api.get(`/dashboard/presenca-por-mes${ano ? `?ano=${ano}` : ''}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Presença por departamento
  async getPresencaPorDepartamento() {
    try {
      const response = await api.get('/dashboard/presenca-por-departamento');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Top colaboradores
  async getTopColaboradores(limite = 5) {
    try {
      const response = await api.get(`/dashboard/top-colaboradores?limite=${limite}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Próximas reuniões
  async getProximasReunioes(limite = 5) {
    try {
      const response = await api.get(`/dashboard/proximas-reunioes?limite=${limite}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Reuniões recentes
  async getReuniaoesRecentes(limite = 5) {
    try {
      const response = await api.get(`/dashboard/reunioes-recentes?limite=${limite}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Alertas do sistema
  async getAlertas() {
    try {
      const response = await api.get('/dashboard/alertas');
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Comparecimento por reunião
  async getComparecimentoPorReuniao(limite = 10) {
    try {
      const response = await api.get(`/dashboard/comparecimento-por-reuniao?limite=${limite}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  // Aniversariantes do mês (categoria, mes e ano opcionais)
  async getAniversariantes(categoria, mes, ano) {
    try {
      const params = new URLSearchParams();
      if (categoria && categoria !== 'todos') params.append('categoria', categoria);
      if (mes != null && mes !== '') params.append('mes', mes);
      if (ano != null && ano !== '') params.append('ano', ano);
      const query = params.toString();
      const response = await api.get(`/dashboard/aniversariantes${query ? `?${query}` : ''}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },
};

// Serviços para Cargos
export const cargoService = {
  // Listar todos os cargos
  async getAll() {
    try {
      const response = await api.get('/cargos');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Listar cargos com colaboradores
  async getAllWithColaboradores() {
    try {
      const response = await api.get('/cargos/with-colaboradores');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Buscar cargo por ID
  async getById(id) {
    try {
      const response = await api.get(`/cargos/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Colaboradores e membros da irmandade vinculados ao cargo
  async getVinculos(id) {
    try {
      const response = await api.get(`/cargos/${id}/vinculos`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Criar novo cargo
  async create(dados) {
    try {
      const response = await api.post('/cargos', dados);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Atualizar cargo
  async update(id, dados) {
    try {
      const response = await api.put(`/cargos/${id}`, dados);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Deletar cargo
  async delete(id) {
    try {
      const response = await api.delete(`/cargos/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Listar apenas cargos ativos
  async getAtivos() {
    try {
      const response = await api.get('/cargos?ativo=true');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Buscar estatísticas de um cargo
  async getEstatisticas(id) {
    try {
      const response = await api.get(`/cargos/${id}/estatisticas`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  }
};

// Serviços para Congregações
export const congregacaoService = {
  // Buscar todas as congregações
  async getAll() {
    try {
      const response = await api.get('/congregacoes');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Buscar congregação por ID
  async getById(id) {
    try {
      const response = await api.get(`/congregacoes/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Criar nova congregação
  async create(dados) {
    try {
      const response = await api.post('/congregacoes', dados);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Atualizar congregação
  async update(id, dados) {
    try {
      const response = await api.put(`/congregacoes/${id}`, dados);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Deletar congregação
  async delete(id) {
    try {
      const response = await api.delete(`/congregacoes/${id}`);
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Buscar apenas congregações ativas
  async getAtivas() {
    try {
      const response = await api.get('/congregacoes?ativo=true');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  },

  // Buscar estatísticas da congregação
  async getEstatisticas() {
    try {
      const response = await api.get('/congregacoes/estatisticas');
      return {
        success: true,
        data: response.data
      };
    } catch (error) {
      throw error;
    }
  }
};

// Vínculo membro da irmandade ↔ cargo (tabela irmandade_cargos)
export const irmandadeMembroCargoService = {
  async listarTodosMembros() {
    try {
      const response = await api.get('/irmandade/todos');
      const data = response.data;
      return {
        success: true,
        data: Array.isArray(data) ? data : []
      };
    } catch (error) {
      throw error;
    }
  },

  async adicionarCargosAoMembro(membroId, cargoIds) {
    try {
      const response = await api.post(`/irmandade/${membroId}/cargos`, { cargo_ids: cargoIds });
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  },

  async removerCargoDoMembro(membroId, cargoId) {
    try {
      const response = await api.delete(`/irmandade/${membroId}/cargos/${cargoId}`);
      return formatApiResponse(response);
    } catch (error) {
      throw error;
    }
  }
};

// Serviços para Mapa / Territorialização
export const mapaService = {
  // Buscar dados para mapa de crianças/jovens
  async getDadosMapa(filtros = {}) {
    try {
      const params = new URLSearchParams();
      if (filtros.categoria) params.append('categoria', filtros.categoria);
      if (filtros.somente_com_coords) params.append('somente_com_coords', filtros.somente_com_coords);
      
      const query = params.toString();
      const response = await api.get(`/irmandade/mapa${query ? `?${query}` : ''}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Análise territorial de frequência (crianças e jovens)
export const analiseFrequenciaService = {
  async buscar(filtros = {}) {
    const params = new URLSearchParams();
    if (filtros.categorias?.length) params.set('categorias', filtros.categorias.join(','));
    if (filtros.status?.length) params.set('status', filtros.status.join(','));
    if (filtros.bairro) params.set('bairro', filtros.bairro);
    if (filtros.busca) params.set('busca', filtros.busca);
    if (filtros.tipo_pessoa) params.set('tipo_pessoa', filtros.tipo_pessoa);
    if (filtros.localizacao === 'com') params.set('somente_com_coordenadas', 'true');
    ['batizado', 'visita_indicada', 'aceitou_visita'].forEach((campo) => {
      if (filtros[campo] !== undefined && filtros[campo] !== '') {
        params.set(campo, filtros[campo]);
      }
    });
    const query = params.toString();
    const response = await api.get(
      `/irmandade/analise-frequencia-territorial${query ? `?${query}` : ''}`
    );
    return response.data;
  },

  async atualizar(id, dados) {
    const response = await api.patch(`/irmandade/${id}/frequencia`, dados);
    return response.data;
  },
};

// Serviços para Recitativos
export const recitativoService = {
  async listarMeus() {
    const response = await api.get('/recitativos?meus=1');
    return response.data;
  },

  async listarTodos() {
    const response = await api.get('/recitativos');
    return response.data;
  },

  async listarFaltas(filtros = {}) {
    const params = new URLSearchParams();
    if (filtros.meus) params.append('meus', '1');
    if (filtros.turma) params.append('turma', filtros.turma);
    if (filtros.tipo_pessoa) params.append('tipo_pessoa', filtros.tipo_pessoa);
    if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio);
    if (filtros.data_fim) params.append('data_fim', filtros.data_fim);
    const qs = params.toString();
    const response = await api.get(`/recitativos/faltas${qs ? `?${qs}` : ''}`);
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/recitativos/${id}`);
    return response.data;
  },

  async criar(dados) {
    const response = await api.post('/recitativos', dados);
    return response.data;
  },

  async excluir(id) {
    const response = await api.delete(`/recitativos/${id}`);
    return response.data;
  },

  async marcarPresenca(recitativoId, entregaId, dados) {
    const response = await api.patch(
      `/recitativos/${recitativoId}/entregas/${entregaId}`,
      dados
    );
    return response.data;
  },

  async fechar(id) {
    const response = await api.patch(`/recitativos/${id}/fechar`);
    return response.data;
  },

  async getRanking(turma, tipoPessoa) {
    const params = new URLSearchParams();
    params.append('turma', turma);
    if (tipoPessoa) params.append('tipoPessoa', tipoPessoa);
    const response = await api.get(`/recitativos/ranking?${params.toString()}`);
    return response.data;
  },
};

// Serviços para Bíblia
export const bibliaService = {
  async listarLivros() {
    const response = await api.get('/biblia/livros');
    return response.data;
  },

  async gerarSequencia(livro, capitulo, verso, total) {
    const response = await api.post('/biblia/sequencia', {
      livro, capitulo, verso, total
    });
    return response.data;
  },
};

// Serviço para Rota de Jovens
export const rotaService = {
  async getRota(origem = {}) {
    try {
      const params = new URLSearchParams();
      if (origem.lat) params.append('lat', origem.lat);
      if (origem.lng) params.append('lng', origem.lng);
      if (origem.nome) params.append('nome', origem.nome);
      const query = params.toString();
      const response = await api.get(`/irmandade/rota-jovens${query ? `?${query}` : ''}`);
      return response.data;
    } catch (error) {
      throw error;
    }
  }
};

// Serviço para Reunião de Jovens e Menores
export const reuniaoJovensService = {
  async listar(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, v);
    });
    const url = `/reuniao-jovens${qs.toString() ? `?${qs}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },

  async getById(id) {
    const response = await api.get(`/reuniao-jovens/${id}`);
    return response.data;
  },

  async criar(dados) {
    const response = await api.post('/reuniao-jovens', dados);
    return response.data;
  },

  async atualizar(id, dados) {
    const response = await api.put(`/reuniao-jovens/${id}`, dados);
    return response.data;
  },

  async excluir(id) {
    const response = await api.delete(`/reuniao-jovens/${id}`);
    return response.data;
  },

  async salvarRelatorio(id, dados) {
    const response = await api.put(`/reuniao-jovens/${id}/relatorio`, dados);
    return response.data;
  },

  async fechar(id) {
    const response = await api.post(`/reuniao-jovens/${id}/fechar`);
    return response.data;
  },

  async uploadFoto(id, arquivo) {
    // fetch nativo evita bug do axios (Content-Type: application/json)
    // que no iPhone Safari corta o multipart → "Unexpected end of form"
    const form = new FormData();
    form.append('foto', arquivo);

    const token = localStorage.getItem('token');
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), 180000);

    try {
      const resp = await fetch(`${api.defaults.baseURL}/reuniao-jovens/${id}/foto`, {
        method: 'POST',
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        body: form,
        signal: controller.signal,
      });

      const data = await resp.json().catch(() => ({}));
      if (!resp.ok) {
        const err = new Error(data.error || 'Erro ao enviar foto');
        err.response = { status: resp.status, data };
        throw err;
      }
      return data;
    } finally {
      clearTimeout(timer);
    }
  },

  async removerFoto(id) {
    const response = await api.delete(`/reuniao-jovens/${id}/foto`);
    return response.data;
  },

  async gerarRecorrentes(dados) {
    const response = await api.post('/reuniao-jovens/gerar-recorrentes', dados);
    return response.data;
  },

  async dashboard(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, v);
    });
    const url = `/reuniao-jovens/dashboard/stats${qs.toString() ? `?${qs}` : ''}`;
    const response = await api.get(url);
    return response.data;
  },
};

// Serviço para Visitas de Aniversário
export const visitaAniversarioService = {
  async aniversariantes(mes, ano) {
    const qs = new URLSearchParams();
    if (mes) qs.append('mes', mes);
    if (ano) qs.append('ano', ano);
    const r = await api.get(`/visitas-aniversario/aniversariantes?${qs.toString()}`);
    return r.data;
  },
  async listar(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v) qs.append(k, v); });
    const r = await api.get(`/visitas-aniversario?${qs.toString()}`);
    return r.data;
  },
  async criar(dados) {
    const r = await api.post('/visitas-aniversario', dados);
    return r.data;
  },
  async atualizar(id, dados) {
    const r = await api.patch(`/visitas-aniversario/${id}`, dados);
    return r.data;
  },
  async excluir(id) {
    const r = await api.delete(`/visitas-aniversario/${id}`);
    return r.data;
  },
  async mensagemIndividual(criancaId, diaSugerido = '') {
    const qs = diaSugerido ? `?diaSugerido=${encodeURIComponent(diaSugerido)}` : '';
    const r = await api.get(`/visitas-aniversario/mensagem-individual/${criancaId}${qs}`);
    return r.data;
  },
  async mensagemGrupo(data) {
    const r = await api.get(`/visitas-aniversario/mensagem-grupo?data=${encodeURIComponent(data)}`);
    return r.data;
  },
};

// Serviço para Perfis de Usuário
export const perfilService = {
  async listar() {
    return api.get('/perfis');
  },
  async getById(id) {
    return api.get(`/perfis/${id}`);
  },
  async criar(dados) {
    return api.post('/perfis', dados);
  },
  async atualizar(id, dados) {
    return api.put(`/perfis/${id}`, dados);
  },
  async excluir(id) {
    return api.delete(`/perfis/${id}`);
  },
};

export const reuniaoCriancasService = {
  async listar(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, v);
    });
    const url = `/reuniao-criancas${qs.toString() ? `?${qs}` : ''}`;
    const r = await api.get(url);
    return r.data;
  },
  async getById(id) {
    const r = await api.get(`/reuniao-criancas/${id}`);
    return r.data;
  },
  async criar(dados) {
    const r = await api.post('/reuniao-criancas', dados);
    return r.data;
  },
  async atualizar(id, dados) {
    const r = await api.put(`/reuniao-criancas/${id}`, dados);
    return r.data;
  },
  async excluir(id) {
    const r = await api.delete(`/reuniao-criancas/${id}`);
    return r.data;
  },
  async marcarPresenca(reuniaoId, dados) {
    const r = await api.post(`/reuniao-criancas/${reuniaoId}/presenca`, dados);
    return r.data;
  },
  async removerPresenca(reuniaoId, irmandadeId) {
    const r = await api.delete(`/reuniao-criancas/${reuniaoId}/presenca/${irmandadeId}`);
    return r.data;
  },
  async salvarColaboradores(reuniaoId, dados) {
    const r = await api.put(`/reuniao-criancas/${reuniaoId}/colaboradores`, dados);
    return r.data;
  },
  async fechar(id) {
    const r = await api.post(`/reuniao-criancas/${id}/fechar`);
    return r.data;
  },
  async gerarRecorrentes(dados) {
    const r = await api.post('/reuniao-criancas/gerar-recorrentes', dados);
    return r.data;
  },
  async dashboard(params = {}) {
    const qs = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== null && v !== '') qs.append(k, v);
    });
    const url = `/reuniao-criancas/dashboard/stats${qs.toString() ? `?${qs}` : ''}`;
    const r = await api.get(url);
    return r.data;
  },
  async criancasDisponiveis() {
    const r = await api.get('/reuniao-criancas/criancas-disponiveis');
    return r.data;
  },
  async frequenciaMembro(irmandadeId) {
    const r = await api.get(`/reuniao-criancas/frequencia/${irmandadeId}`);
    return r.data;
  },
};

export const logAlteracaoService = {
  async listar(membroId) {
    const r = await api.get(`/irmandade/${membroId}/logs`);
    return r.data;
  },
};

export default api;